import { useEffect, useState } from "react";

type ShareModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  linkText?: string;
};

export default function ShareModal({ open, onClose, title, linkText = "复制链接" }: ShareModalProps) {
  const [url, setUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [wechatTip, setWechatTip] = useState(false);
  const [copyError, setCopyError] = useState("");

  useEffect(() => {
    if (open) {
      setUrl(window.location.href);
      setCopied(false);
      setWechatTip(false);
      setCopyError("");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setCopyError("");
    } catch {
      setCopied(false);
      setCopyError("当前浏览器不允许自动复制，请手动选中链接复制。");
    }
  };

  const handleWeibo = () => {
    const shareUrl = `https://service.weibo.com/share/share.php?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`;
    window.open(shareUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-foreground-950/60 px-4 pb-4 pt-20 sm:items-center sm:pb-20" role="dialog" aria-modal="true" aria-label="分享">
      <button type="button" aria-label="关闭分享弹窗" onClick={onClose} className="absolute inset-0 cursor-pointer" />
      <div className="relative z-10 w-full max-w-md rounded-lg bg-background-50 p-5 md:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-foreground-950">分享给朋友</h3>
            <p className="mt-1 line-clamp-1 text-xs text-foreground-500">{title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭"
            className="grid h-8 w-8 cursor-pointer place-items-center rounded-md bg-background-100 text-foreground-600 transition-colors hover:bg-background-200"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="mt-5 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2">
          <input
            readOnly
            value={url}
            aria-label="分享链接"
            onFocus={(event) => event.currentTarget.select()}
            className="w-full bg-transparent font-label text-xs text-foreground-700 focus:outline-none"
          />
          <button
            type="button"
            onClick={handleCopy}
            className="shrink-0 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            {copied ? "已复制" : linkText}
          </button>
        </div>

        {copyError && <p className="mt-2 text-xs text-accent-700">{copyError}</p>}

        <div className="mt-5 grid grid-cols-3 gap-3">
          <button
            type="button"
            onClick={handleWeibo}
            className="flex cursor-pointer flex-col items-center gap-2 rounded-md border border-background-200 bg-background-50 py-3 transition-colors hover:border-primary-300"
          >
            <span className="grid h-10 w-10 place-items-center rounded-full bg-accent-100 text-accent-800">
              <i className="ri-weibo-fill text-lg" />
            </span>
            <span className="text-xs text-foreground-700">新浪微博</span>
          </button>
          <button
            type="button"
            onClick={() => setWechatTip(true)}
            className="flex cursor-pointer flex-col items-center gap-2 rounded-md border border-background-200 bg-background-50 py-3 transition-colors hover:border-primary-300"
          >
            <span className="grid h-10 w-10 place-items-center rounded-full bg-primary-100 text-primary-700">
              <i className="ri-wechat-fill text-lg" />
            </span>
            <span className="text-xs text-foreground-700">微信</span>
          </button>
          <button
            type="button"
            onClick={handleCopy}
            className="flex cursor-pointer flex-col items-center gap-2 rounded-md border border-background-200 bg-background-50 py-3 transition-colors hover:border-primary-300"
          >
            <span className="grid h-10 w-10 place-items-center rounded-full bg-secondary-100 text-secondary-800">
              <i className="ri-link" />
            </span>
            <span className="text-xs text-foreground-700">复制链接</span>
          </button>
        </div>

        {wechatTip && (
          <p className="mt-3 rounded-md bg-secondary-100 px-3 py-2 text-xs leading-6 text-secondary-900">
            微信暂不支持网页内直接分享，请点击「复制链接」后在微信中粘贴发送。
          </p>
        )}

        <p className="mt-4 text-center text-[11px] text-foreground-400">分享功能为前端演示，不会上传任何数据</p>
      </div>
    </div>
  );
}